import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import NewItemButton from "../../components/ui/NewItemButton";
import StatusBadge from "../../components/ui/StatusBadge";
import AdminSearchableList from "../../components/admin/AdminSearchableList";
import FormModal from "../../components/admin/FormModal";
import CustomerForm from "../../components/admin/CustomerForm";
import { useBackNavigation } from "../../navigation/BackNavigationContext";

export default function CustomersListPage() {
  const navigate = useNavigate();
  const { markAsRoot } = useBackNavigation();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    markAsRoot(true);
  }, [markAsRoot]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/admin/customers", {
        params: { search: debounced, page, limit },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, page]);

  const totalPages = Math.ceil(total / limit);

  const handleSave = async (values) => {
    if (editing) {
      await axiosInstance.put(`/admin/customers/${editing._id}`, values);
    } else {
      await axiosInstance.post("/admin/customers", values);
    }
    setModalOpen(false);
    setEditing(null);
    fetchItems();
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (c) => (
        <span className="text-blue-600 font-medium">{c.name}</span>
      ),
    },
    { key: "slug", label: "Slug" },
    {
      key: "status",
      label: "Status",
      render: (c) => <StatusBadge status={c.status || "active"} />,
    },
    {
      key: "organisationCount",
      label: "Departments",
      render: (c) => c.organisationCount ?? 0,
    },
    { key: "billingEmail", label: "Billing email", render: (c) => c.billingEmail || "—" },
    {
      key: "actions",
      label: "",
      render: (c) => (
        <button
          type="button"
          className="text-sm text-gray-600 hover:text-gray-900"
          onClick={(e) => {
            e.stopPropagation();
            setEditing(c);
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Customers"
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e.target.value);
            setPage(1);
          },
          placeholder: "Search by name or slug",
          onClear: () => {
            setSearch("");
            setPage(1);
          },
        }}
        actions={
          <NewItemButton
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            label="New customer"
            shortLabel="New"
          />
        }
      />
      <PageWrapper>
        <AdminSearchableList
          items={items}
          loading={loading}
          error={error}
          columns={columns}
          onRowClick={(c) => navigate(`/admin/customers/${c._id}`)}
          emptyMessage="No customers found."
          pagination={{
            page,
            totalPages,
            total,
            onPrev: () => setPage((p) => Math.max(1, p - 1)),
            onNext: () => setPage((p) => p + 1),
          }}
        />
      </PageWrapper>

      <FormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        title={editing ? "Edit customer" : "New customer"}
      >
        <CustomerForm
          initialValues={editing || {}}
          onSubmit={handleSave}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          submitLabel={editing ? "Save changes" : "Create customer"}
        />
      </FormModal>
    </>
  );
}
